import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Firebase Setup ─────────────────────────────────────────────────────────
import { initializeApp } from "firebase/app";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    getFirestore,
    orderBy,
    query,
    serverTimestamp,
} from "firebase/firestore";

// 🔥 Replace these with YOUR Firebase project config from:
//    https://console.firebase.google.com  →  Project Settings  →  General  →  Your apps
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBKS33UQwMQ7TG_4j18KfnyGJBxRKbdE1k",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "sample-96a06.firebaseapp.com",
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL || "https://sample-96a06-default-rtdb.firebaseio.com",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "sample-96a06",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "sample-96a06.firebasestorage.app",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "955000310644",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:955000310644:web:20361259fe3f0a3acffa21",
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-S7XE52CFSC",
};

// 🔒 Firestore Rules — paste these in the Firebase Console → Firestore → Rules:
//
//   rules_version = '2';
//   service cloud.firestore {
//     match /databases/{database}/documents {
//       match /files/{fileId} {
//         allow read, write: if true;
//       }
//     }
//   }

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const FILES_COLLECTION = "files";

// ─── Inline Styles (Glassmorphism Dark Theme) ───────────────────────────────

const COLORS = {
    bg: "#0D1117",
    card: "#161B22",
    text: "#C9D1D9",
    muted: "#8B949E",
    accent: "#58A6FF",
    green: "#238636",
    greenHover: "#2EA043",
    red: "#DA3633",
    border: "#30363D",
};

const styles = {
    /* ── Page wrapper ─────────────────────────────────────── */
    page: {
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${COLORS.bg} 0%, ${COLORS.card} 100%)`,
        color: COLORS.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
        padding: "80px 20px 40px",
        position: "relative",
        overflow: "hidden",
    },

    /* ── Back button ──────────────────────────────────────── */
    backBtn: {
        position: "fixed",
        top: 16,
        left: 16,
        zIndex: 1100,
        background: "rgba(255,255,255,0.08)",
        backdropFilter: "blur(12px)",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 50,
        padding: "10px 22px",
        color: COLORS.accent,
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 8,
        transition: "all .3s ease",
    },

    /* ── Tab bar (Creator / Dashboard) ────────────────────── */
    tabs: {
        display: "flex",
        justifyContent: "center",
        gap: 12,
        marginBottom: 36,
    },
    tab: active => ({
        padding: "10px 28px",
        borderRadius: 50,
        border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
        background: active ? "rgba(88,166,255,0.15)" : "rgba(255,255,255,0.04)",
        color: active ? COLORS.accent : COLORS.muted,
        cursor: "pointer",
        fontWeight: 600,
        fontSize: 14,
        transition: "all .3s ease",
        backdropFilter: "blur(8px)",
    }),

    /* ── Glass card ───────────────────────────────────────── */
    card: {
        maxWidth: 780,
        margin: "0 auto",
        background: "rgba(22,27,34,0.65)",
        backdropFilter: "blur(20px)",
        borderRadius: 20,
        border: `1px solid ${COLORS.border}`,
        padding: "36px 32px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    },

    /* ── Inputs ───────────────────────────────────────────── */
    label: {
        display: "block",
        marginBottom: 8,
        fontSize: 13,
        fontWeight: 600,
        color: COLORS.muted,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
    },
    input: {
        width: "100%",
        padding: "12px 16px",
        background: "rgba(255,255,255,0.05)",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        color: COLORS.text,
        fontSize: 15,
        outline: "none",
        transition: "border-color .3s",
        boxSizing: "border-box",
    },
    textarea: {
        width: "100%",
        minHeight: 320,
        padding: "16px",
        background: "rgba(13,17,23,0.8)",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        color: COLORS.text,
        fontSize: 14,
        fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
        lineHeight: 1.7,
        resize: "vertical",
        outline: "none",
        transition: "border-color .3s",
        boxSizing: "border-box",
    },

    /* ── Buttons ──────────────────────────────────────────── */
    btnRow: {
        display: "flex",
        gap: 14,
        marginTop: 24,
        flexWrap: "wrap",
    },
    btnPrimary: {
        flex: 1,
        minWidth: 160,
        padding: "14px 24px",
        borderRadius: 50,
        border: "none",
        background: `linear-gradient(135deg, ${COLORS.green}, ${COLORS.greenHover})`,
        color: "#fff",
        fontWeight: 700,
        fontSize: 15,
        cursor: "pointer",
        transition: "all .3s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
    },
    btnSecondary: {
        flex: 1,
        minWidth: 160,
        padding: "14px 24px",
        borderRadius: 50,
        border: `2px solid ${COLORS.accent}`,
        background: "transparent",
        color: COLORS.accent,
        fontWeight: 700,
        fontSize: 15,
        cursor: "pointer",
        transition: "all .3s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
    },
    btnDanger: {
        padding: "8px 16px",
        borderRadius: 50,
        border: `1px solid ${COLORS.red}`,
        background: "transparent",
        color: COLORS.red,
        fontWeight: 600,
        fontSize: 13,
        cursor: "pointer",
        transition: "all .3s ease",
    },

    /* ── Dashboard grid ───────────────────────────────────── */
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 20,
    },
    fileCard: {
        background: "rgba(22,27,34,0.65)",
        backdropFilter: "blur(16px)",
        borderRadius: 16,
        border: `1px solid ${COLORS.border}`,
        padding: "22px 20px",
        transition: "transform .3s ease, box-shadow .3s ease",
        cursor: "default",
    },
    fileCardHover: {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 36px rgba(0,0,0,0.5)",
    },
    fileName: {
        fontSize: 16,
        fontWeight: 700,
        color: COLORS.accent,
        wordBreak: "break-all",
        marginBottom: 6,
    },
    fileMeta: {
        fontSize: 12,
        color: COLORS.muted,
        marginBottom: 14,
    },
    fileActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
    },
    smallBtn: color => ({
        padding: "7px 16px",
        borderRadius: 50,
        border: `1px solid ${color}`,
        background: "transparent",
        color: color,
        fontWeight: 600,
        fontSize: 12,
        cursor: "pointer",
        transition: "all .3s ease",
    }),

    /* ── Toast ────────────────────────────────────────────── */
    toastContainer: {
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
    },
    toast: type => ({
        padding: "14px 22px",
        borderRadius: 12,
        color: "#fff",
        fontWeight: 600,
        fontSize: 14,
        backdropFilter: "blur(12px)",
        border: `1px solid ${type === "success" ? COLORS.green : type === "error" ? COLORS.red : COLORS.accent}`,
        background:
            type === "success"
                ? "rgba(35,134,54,0.85)"
                : type === "error"
                  ? "rgba(218,54,51,0.85)"
                  : "rgba(88,166,255,0.85)",
        animation: "slideIn .3s ease",
        maxWidth: 340,
        boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
    }),

    /* ── Draft banner ─────────────────────────────────────── */
    draftBanner: {
        background: "rgba(88,166,255,0.12)",
        border: `1px solid ${COLORS.accent}`,
        borderRadius: 12,
        padding: "12px 18px",
        marginBottom: 20,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: 13,
        color: COLORS.accent,
    },

    /* ── Empty state ──────────────────────────────────────── */
    empty: {
        textAlign: "center",
        padding: "80px 20px",
        color: COLORS.muted,
    },
    emptyIcon: {
        fontSize: 56,
        marginBottom: 16,
        opacity: 0.4,
    },

    /* ── Heading ──────────────────────────────────────────── */
    heading: {
        textAlign: "center",
        fontSize: 28,
        fontWeight: 700,
        background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.green})`,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        marginBottom: 8,
    },
    subheading: {
        textAlign: "center",
        fontSize: 14,
        color: COLORS.muted,
        marginBottom: 32,
    },

    /* ── Loading spinner ──────────────────────────────────── */
    spinner: {
        width: 36,
        height: 36,
        border: `3px solid ${COLORS.border}`,
        borderTop: `3px solid ${COLORS.accent}`,
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        margin: "60px auto",
    },
};

// Inject keyframe animations once
const KEYFRAMES_ID = "fc-keyframes";
if (typeof document !== "undefined" && !document.getElementById(KEYFRAMES_ID)) {
    const sheet = document.createElement("style");
    sheet.id = KEYFRAMES_ID;
    sheet.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
    document.head.appendChild(sheet);
}

// ─── Toast Hook ──────────────────────────────────────────────────────────────

function useToasts() {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);

    const addToast = useCallback((message, type = "info", duration = 3500) => {
        const id = ++idRef.current;
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, []);

    const ToastContainer = () => (
        <div style={styles.toastContainer}>
            {toasts.map(t => (
                <div key={t.id} style={styles.toast(t.type)}>
                    {t.type === "success" ? "✓ " : t.type === "error" ? "✗ " : "ℹ "}
                    {t.message}
                </div>
            ))}
        </div>
    );

    return { addToast, ToastContainer };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DRAFT_KEY = "fc_draft";

function saveDraft(filename, content) {
    try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ filename, content, ts: Date.now() }));
    } catch {
        // silently fail
    }
}

function loadDraft() {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function clearDraft() {
    localStorage.removeItem(DRAFT_KEY);
}

function downloadBlob(filename, content) {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "untitled.txt";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

function formatDate(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatSize(len) {
    if (len < 1024) return `${len} B`;
    if (len < 1024 * 1024) return `${(len / 1024).toFixed(1)} KB`;
    return `${(len / (1024 * 1024)).toFixed(2)} MB`;
}

// ─── FileCard Sub-component ──────────────────────────────────────────────────

function FileCard({ file, onDownload, onDelete, onOpen }) {
    const [hovered, setHovered] = React.useState(false);

    return (
        <div
            style={{
                ...styles.fileCard,
                ...(hovered ? styles.fileCardHover : {}),
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={styles.fileName}>📄 {file.filename}</div>
            <div style={styles.fileMeta}>
                {formatDate(file.createdAt)}
                {file.size != null && ` · ${formatSize(file.size)}`}
            </div>
            <div style={styles.fileActions}>
                <button style={styles.smallBtn(COLORS.accent)} onClick={() => onOpen(file)}>
                    Open
                </button>
                <button style={styles.smallBtn(COLORS.green)} onClick={() => onDownload(file)}>
                    Download
                </button>
                <button style={styles.smallBtn(COLORS.red)} onClick={() => onDelete(file)}>
                    Delete
                </button>
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function FileCreation() {
    const navigate = useNavigate();
    const { addToast, ToastContainer } = useToasts();

    // Tab state
    const [view, setView] = useState("creator"); // "creator" | "dashboard"

    // Creator state
    const [filename, setFilename] = useState("");
    const [content, setContent] = useState("");
    const [saving, setSaving] = useState(false);
    const [draftAvailable, setDraftAvailable] = useState(false);

    // Dashboard state
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    // ── Draft Auto-save (debounced) ──────────────────────────
    const debounceRef = useRef(null);

    useEffect(() => {
        if (!filename && !content) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            saveDraft(filename, content);
        }, 1000);
        return () => clearTimeout(debounceRef.current);
    }, [filename, content]);

    // ── Check for existing draft on mount ────────────────────
    useEffect(() => {
        const draft = loadDraft();
        if (draft && (draft.filename || draft.content)) {
            setDraftAvailable(true);
        }
    }, []);

    function restoreDraft() {
        const draft = loadDraft();
        if (draft) {
            setFilename(draft.filename || "");
            setContent(draft.content || "");
            setDraftAvailable(false);
            addToast("Draft restored!", "success");
        }
    }

    function dismissDraft() {
        clearDraft();
        setDraftAvailable(false);
    }

    // ── Firestore: Fetch all files ───────────────────────────
    const fetchFiles = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(collection(db, FILES_COLLECTION), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setFiles(data);
        } catch (err) {
            console.error("Fetch error:", err);
            addToast("Failed to load files. Check Firebase config.", "error");
        } finally {
            setLoading(false);
        }
    }, [addToast]);

    // Fetch files when switching to dashboard
    useEffect(() => {
        if (view === "dashboard") {
            fetchFiles();
        }
    }, [view, fetchFiles]);

    // ── Firestore: Save file ─────────────────────────────────
    async function handleSaveToCloud() {
        if (!filename.trim()) {
            addToast("Please enter a filename.", "error");
            return;
        }
        if (!content.trim()) {
            addToast("File content is empty.", "error");
            return;
        }

        setSaving(true);
        try {
            await addDoc(collection(db, FILES_COLLECTION), {
                filename: filename.trim(),
                content: content,
                size: new Blob([content]).size,
                createdAt: serverTimestamp(),
            });
            addToast(`"${filename.trim()}" saved to cloud!`, "success");
            clearDraft();
            setFilename("");
            setContent("");
            setDraftAvailable(false);
        } catch (err) {
            console.error("Save error:", err);
            addToast("Save failed. Check your Firebase config.", "error");
        } finally {
            setSaving(false);
        }
    }

    // ── Local download ───────────────────────────────────────
    function handleDownload() {
        if (!filename.trim()) {
            addToast("Please enter a filename.", "error");
            return;
        }
        if (!content.trim()) {
            addToast("File content is empty.", "error");
            return;
        }
        downloadBlob(filename.trim(), content);
        addToast(`"${filename.trim()}" downloaded!`, "success");
    }

    // ── Dashboard actions ────────────────────────────────────
    function handleFileDownload(file) {
        downloadBlob(file.filename, file.content);
        addToast(`Downloaded "${file.filename}"`, "success");
    }

    async function handleFileDelete(file) {
        try {
            await deleteDoc(doc(db, FILES_COLLECTION, file.id));
            setFiles(prev => prev.filter(f => f.id !== file.id));
            addToast(`Deleted "${file.filename}"`, "success");
        } catch (err) {
            console.error("Delete error:", err);
            addToast("Delete failed.", "error");
        }
    }

    function handleFileOpen(file) {
        setFilename(file.filename);
        setContent(file.content);
        setView("creator");
        addToast(`Opened "${file.filename}"`, "info");
    }

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={styles.page}>
            <ToastContainer />

            {/* Back to portfolio */}
            <button
                style={styles.backBtn}
                onClick={() => navigate("/")}
                onMouseEnter={e => (e.currentTarget.style.background = "rgba(88,166,255,0.15)")}
                onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
            >
                ← Portfolio
            </button>

            {/* Page heading */}
            <h1 style={styles.heading}>File Creator</h1>
            <p style={styles.subheading}>Create text-based files — download locally or save to the cloud</p>

            {/* Tabs */}
            <div style={styles.tabs}>
                <button style={styles.tab(view === "creator")} onClick={() => setView("creator")}>
                    ✏️ Creator
                </button>
                <button style={styles.tab(view === "dashboard")} onClick={() => setView("dashboard")}>
                    📁 Dashboard
                </button>
            </div>

            {/* ── Creator View ────────────────────────────────── */}
            {view === "creator" && (
                <div style={styles.card}>
                    {/* Draft banner */}
                    {draftAvailable && (
                        <div style={styles.draftBanner}>
                            <span>📝 Unsaved draft found</span>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    style={{
                                        ...styles.smallBtn(COLORS.accent),
                                        fontWeight: 700,
                                    }}
                                    onClick={restoreDraft}
                                >
                                    Restore
                                </button>
                                <button style={styles.smallBtn(COLORS.muted)} onClick={dismissDraft}>
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Filename */}
                    <div style={{ marginBottom: 20 }}>
                        <label style={styles.label}>Filename</label>
                        <input
                            style={styles.input}
                            type="text"
                            placeholder="e.g. notes.txt, script.js, data.json"
                            value={filename}
                            onChange={e => setFilename(e.target.value)}
                            onFocus={e => (e.target.style.borderColor = COLORS.accent)}
                            onBlur={e => (e.target.style.borderColor = COLORS.border)}
                        />
                    </div>

                    {/* Content */}
                    <div style={{ marginBottom: 4 }}>
                        <label style={styles.label}>Content</label>
                        <textarea
                            style={styles.textarea}
                            placeholder="Start typing your file content here..."
                            value={content}
                            onChange={e => setContent(e.target.value)}
                            onFocus={e => (e.target.style.borderColor = COLORS.accent)}
                            onBlur={e => (e.target.style.borderColor = COLORS.border)}
                        />
                    </div>

                    {/* Char count */}
                    <div
                        style={{
                            textAlign: "right",
                            fontSize: 12,
                            color: COLORS.muted,
                            marginBottom: 8,
                        }}
                    >
                        {content.length} characters · {formatSize(new Blob([content]).size)}
                    </div>

                    {/* Action buttons */}
                    <div style={styles.btnRow}>
                        <button
                            style={styles.btnPrimary}
                            onClick={handleDownload}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 10px 25px rgba(35,134,54,0.3)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                        >
                            ⬇ Download Locally
                        </button>
                        <button
                            style={styles.btnSecondary}
                            onClick={handleSaveToCloud}
                            disabled={saving}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = "rgba(88,166,255,0.15)";
                                e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.transform = "translateY(0)";
                            }}
                        >
                            {saving ? "Saving…" : "☁ Save to Cloud"}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Dashboard View ──────────────────────────────── */}
            {view === "dashboard" && (
                <div style={{ maxWidth: 900, margin: "0 auto" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 24,
                        }}
                    >
                        <span style={{ fontSize: 14, color: COLORS.muted }}>
                            {files.length} file{files.length !== 1 ? "s" : ""} in cloud
                        </span>
                        <button style={styles.smallBtn(COLORS.accent)} onClick={fetchFiles}>
                            ↻ Refresh
                        </button>
                    </div>

                    {loading ? (
                        <div style={styles.spinner} />
                    ) : files.length === 0 ? (
                        <div style={styles.empty}>
                            <div style={styles.emptyIcon}>📂</div>
                            <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No files yet</p>
                            <p style={{ fontSize: 14 }}>Create a file and save it to the cloud to see it here.</p>
                        </div>
                    ) : (
                        <div style={styles.grid}>
                            {files.map(file => (
                                <FileCard
                                    key={file.id}
                                    file={file}
                                    onDownload={handleFileDownload}
                                    onDelete={handleFileDelete}
                                    onOpen={handleFileOpen}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
